import {AdminInputRow, AdminInputText} from "../../../layout/admin";
import React, {useRef, useState} from "react";
import {EMPTY_RANGES, formatRanges, parseRangesText, Ranges} from "../types/Ranges";
import artisticObjectsService from "../services/artisticObjectsService";
import {Button} from "primereact/button";
import {handleError} from "../../../common/utils/errorHandling";
import {useTranslation} from "react-i18next";


interface CreationTimeProps {
  creationTime: string;
  creationRanges: Ranges;
  onChange: (creationTime: string, ranges: Ranges) => void;
}

const CreationTime: React.FC<CreationTimeProps> = ({creationTime, onChange, creationRanges}) => {
  const {t} = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableRangesText, setEditableRangesText] = useState('');
  const originalCreationTime = useRef<string>('');

  const rangesText = formatRanges(creationRanges);

  // Use editable text when in edit mode, otherwise use computed rangesText
  const displayRangesText = isEditMode ? editableRangesText : rangesText;
  // console.log('displayRangesText', displayRangesText, "from", creationRanges);

  // Show warning if creationTime is non-empty but ranges is empty (validation failed)
  const hasValidationWarning = () => {
    return creationTime.trim() && creationRanges.ranges.length === 0;
  };

  const handleCreationTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCreationTime = e.target.value;

    // Only update the creation time, don't validate yet
    handleOnChange(newCreationTime, creationRanges);
  };

  const handleCreationTimeFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
    originalCreationTime.current = e.target.value;
  };

  const handleOnChange = (creationTime: string, ranges: Ranges): void => {
    console.log('handleOnChange', creationTime, ranges);
    onChange(creationTime, ranges);
  }

  const validateAndSet = (newCreationTime: string): void => {
    handleError(
      async () => {
        const response = await artisticObjectsService.validateDateRange(newCreationTime);
        handleOnChange(newCreationTime, response.ranges);
      },
      t('admin.artisticObjects.errors.validatingDate'),
      () => {
        handleOnChange(newCreationTime, EMPTY_RANGES);
      }
    )
  }

  const handleCreationTimeBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    const newCreationTime = e.target.value;
    console.log('handleCreationTimeBlur', newCreationTime, originalCreationTime.current, !newCreationTime);

    if (newCreationTime === originalCreationTime.current) {
      console.log("Ignoring");
      return;
    }

    if (!newCreationTime) {
      console.log("Clearing");
      handleOnChange(newCreationTime, EMPTY_RANGES);
      setIsEditMode(false);
      return;
    }

    validateAndSet(newCreationTime);

  };

  const handleRangesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newRangesText = e.target.value;
    setEditableRangesText(newRangesText);

    // Parse the manually edited text and submit to backend
    const parsedRanges = parseRangesText(newRangesText);
    console.log("handleRangesChange", parsedRanges);
    handleOnChange(creationTime, parsedRanges);
  };

  const handleEditClick = () => {
    setIsEditMode(true);

    // Copy text logic: if ranges is empty, copy from creationTime; if non-empty, leave as-is
    if (creationRanges.ranges.length === 0 && creationTime.trim()) {
      // If ranges is empty, copy exact text from creationTime field
      setEditableRangesText(creationTime.trim());
      validateAndSet(creationTime.trim());
    } else {
      // If ranges is non-empty, leave text as-is
      setEditableRangesText(rangesText);
    }
  };

  const handleRangesBlur = () => {
    if (!displayRangesText || displayRangesText === creationTime) {
      setIsEditMode(false);
    }
    if (!displayRangesText && creationTime.trim()) {
      validateAndSet(creationTime.trim());
    }
  };

  return (
    <AdminInputRow>
      <AdminInputText
        label={t('admin.creationTime.label')}
        value={creationTime}
        onChange={handleCreationTimeChange}
        onFocus={handleCreationTimeFocus}
        onBlur={handleCreationTimeBlur}
        name="creationTime"
        id="creationTime"
      />
      <div>
        <label htmlFor="ranges" className="block text-sm font-medium hover:text-gray-700 mb-2">
          {t('admin.creationTime.ranges')}
        </label>
        <div className="relative">
          <input
            id="ranges"
            name="ranges"
            type="text"
            value={displayRangesText}
            onChange={handleRangesChange}
            onBlur={handleRangesBlur}
            disabled={!isEditMode}
            className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition duration-300 disabled:bg-gray-100 disabled:text-gray-500"
          />
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
            {hasValidationWarning() && (
              <i className="fa fa-exclamation-triangle text-black hover:text-gray-700 translate-y-px"
                 title={t('admin.creationTime.tooltips.validationWarning')}></i>
            )}
            <Button
              type="button"
              icon="fa fa-edit"
              size="small"
              text
              className="p-1 text-black hover:text-gray-700 focus:outline-none"
              onClick={handleEditClick}
              title={t('admin.creationTime.tooltips.editManually')}
            />
          </div>
        </div>
      </div>
    </AdminInputRow>
  );
};

export default CreationTime;
